import { FIELD_LABELS } from '../email/fieldDictionary';
import type { Company } from '../shared/companies';
import { wallClockDateTime } from '../shared/clientTime';

/**
 * Historia zmian prezlecenia po ludzku.
 *
 * `PreOrderChange` trzyma nazwy kolumn i wartości tekstem — tak, jak zapisał
 * je serwer (`loadingDate — → 2026-10-03T09:30:00.000Z`, `companyId — → 2`,
 * `utworzenie — → MANUAL`). To są dane audytu i zostają w bazie bez zmian;
 * tłumaczymy je dopiero na ekranie (e2e na dev, 28.09.2026).
 */

/** Pola nagłówka (ręczne wprowadzenie) i zdarzenia koperty. */
const HEADER_LABELS: Record<string, string> = {
  companyId: 'Spółka',
  clientName: 'Klient',
  relation: 'Relacja',
  loadingCity: 'Miasto załadunku',
  unloadingCity: 'Miasto rozładunku',
  loadingCountry: 'Kraj załadunku',
  loadingPostalCode: 'Kod pocztowy załadunku',
  unloadingCountry: 'Kraj rozładunku',
  unloadingPostalCode: 'Kod pocztowy rozładunku',
  loadingDate: 'Data załadunku',
  unloadingDate: 'Data rozładunku',
  cargo: 'Ładunek',
  weight: 'Waga (kg)',
  pallets: 'Palety',
  price: 'Cena',
  currency: 'Waluta',
  responsibleDept: 'Dział',
  customerOrderNumber: 'Nr zamówienia klienta',
  requiredVehicleType: 'Wymagany typ pojazdu',
  utworzenie: 'Utworzenie',
  akceptacja: 'Akceptacja — numer zlecenia',
  // Kopia z porównaniem (makieta back office v2, 20.10.2026).
  copiedFrom: 'Skopiowane z prezlecenia',
  copiedTo: 'Skopiowano do prezlecenia',
  copyConfirmed: 'Potwierdzone przy kopii bez zmian',
  // Obieg CS → Administracja (makieta back office v2, fala B, 21.10.2026).
  intakeStage: 'Obieg CS → Administracja',
  viaWriteStatus: 'Zapis do VIA',
  // Kontrahent z kartoteki (makieta back office v2, fala B, 21.10.2026).
  contractorId: 'Kontrahent z kartoteki',
  // Decyzje Administracji w oknie kontroli (26.10.2026, S-26.10-06/-07).
  intakeDecision: 'Decyzja Administracji (okno kontroli)',
  // Pytanie do klienta (26.10.2026, pakiet P1, `clientQuestions`).
  clientQuestion: 'Pytanie do klienta',
  // Zapytanie klienta, dostępność aut i wycena (26.10.2026, P3).
  kind: 'Rodzaj rekordu',
  inquiryStatus: 'Zapytanie — stan',
  inquiryIndicatedCar: 'Zapytanie — auto wskazane wstępnie',
  inquiryForwardedToFtl: 'Zapytanie — przekazane do FTL',
  inquiryFtlResponse: 'Zapytanie — odpowiedź FTL',
  inquiryTradeSignal: 'Zapytanie — sygnał do Trade',
  inquiryConfirmationSent: 'Zapytanie — potwierdzenie dla klienta',
  inquiryQuote: 'Wycena — propozycja ceny',
  inquiryQuoteApproved: 'Wycena — cena zatwierdzona',
  inquiryOfferSent: 'Wycena — oferta',
  inquiryOutcome: 'Wynik oferty',
  // Pliki od klienta (26.10.2026, `preOrderAttachments`).
  zalacznik: 'Plik od klienta dołączony',
  zalacznikUsuniety: 'Plik od klienta usunięty',
  // Maile prezlecenia (03.11.2026, `preOrderDocuments`) — dane, jak pola wyżej.
  mailPowiazany: 'Mail powiązany',
  mailOdpiety: 'Mail odpięty',
};

/** Stany zapytania (`PreOrderInquiry.status`) — kody z API, na ekranie słowem. */
const INQUIRY_STATUS_LABELS: Record<string, string> = {
  to_check: 'do sprawdzenia',
  priced: 'wycenione',
  offer_sent: 'oferta wysłana',
  accepted: 'przyjęte',
  rejected: 'odrzucone',
  no_response: 'brak odpowiedzi',
  declined: 'odmowa',
};

/** Etapy obiegu (`PreOrderChange.intakeStage`) — kody z API, na ekranie słowem. */
const INTAKE_STAGE_LABELS: Record<string, string> = {
  cs: 'u CS',
  administration: 'u Administracji',
  returned_to_cs: 'zwrócone do CS z pytaniem',
};

/** Stany pytania do klienta (`client-communication.ts`) — kody z API, na ekranie słowem. */
const QUESTION_STATE_LABELS: Record<string, string> = {
  draft: 'szkic',
  sent_manually: 'wysłane ręcznie — czeka na odpowiedź',
  answered: 'odpowiedź wpisana',
  cancelled: 'odwołane',
};

/** Stan zapisu do VIA cofnięty zwrotem do CS — kody jak na ekranie zlecenia. */
const WRITE_STATE_LABELS: Record<string, string> = {
  DRAFT: 'szkic',
  APPROVED: 'zatwierdzone do zapisu',
  FAILED: 'błąd zapisu',
};

/**
 * Wpisy-zdarzenia z jedną wartością (bez „stare → nowe”): utworzenie,
 * akceptacja i ślad kopii.
 */
export const SINGLE_VALUE_FIELDS: readonly string[] = [
  'utworzenie',
  'akceptacja',
  'copiedFrom',
  'copiedTo',
  'copyConfirmed',
  'intakeDecision',
  'zalacznik',
  'mailPowiazany',
];

/** Wpisy „wartość — powód” (usunięcie pliku od klienta, 26.10.2026). */
export const REASON_FIELDS: readonly string[] = ['zalacznikUsuniety', 'mailOdpiety'];

/** Pola pliku dla importera na prezleceniu (`structure.<ścieżka>`, 03.11.2026). */
const STRUCTURE_LABELS: Record<string, string> = {
  idZewnetrzny: 'id w systemie klienta',
  nazwa: 'nazwa',
  adres: 'ulica i numer',
  kod: 'kod pocztowy',
  miasto: 'miasto',
  kraj: 'kraj',
  id: 'id punktu',
  companyName: 'firma',
  address: 'ulica i numer',
  zip: 'kod',
  city: 'miasto',
  countryId: 'kraj',
  fromDate: 'okno od (dzień)',
  fromTime: 'okno od (godz.)',
  untilDate: 'okno do (dzień)',
  untilTime: 'okno do (godz.)',
  transportUnitName: 'jednostka',
  transportUnitCount: 'liczba jednostek',
  price: 'cena',
  count: 'liczba czynności',
};

/** „Dane dla importera: punkt 2, kod” z `structure.stacje.1.zip`; inne pola — `null`. */
export function structureFieldLabel(field: string): string | null {
  const m = /^structure\.(.+)$/.exec(field);
  if (!m) return null;
  const parts = m[1].split('.');
  if (parts[0] === 'objetosc') return 'Dane dla importera: objętość';
  if (parts[0] === 'zleceniodawca') return `Dane dla importera: zleceniodawca, ${STRUCTURE_LABELS[parts[1]] ?? parts[1]}`;
  if (parts[0] === 'stacje') return `Dane dla importera: punkt ${Number(parts[1]) + 1}, ${STRUCTURE_LABELS[parts[2]] ?? parts[2]}`;
  if (parts[0] === 'forklift') return `Dane dla importera: czynność wózkowa, ${STRUCTURE_LABELS[parts[1]] ?? parts[1]}`;
  return `Dane dla importera: ${m[1]}`;
}

const DATE_FIELDS = new Set(['loadingDate', 'unloadingDate']);

/** Skąd przyszła zmiana (`PreOrderChange.source`). */
const SOURCE_LABELS: Record<string, string> = {
  MANUAL: 'ręcznie',
  AI: 'ekstrakcja AI',
  RULE: 'reguła',
  VIA: 'VIA',
  COPY: 'kopia z porównaniem',
};

export function changeFieldLabel(field: string): string {
  return HEADER_LABELS[field] ?? FIELD_LABELS[field] ?? structureFieldLabel(field) ?? field;
}

export function changeSourceLabel(source?: string | null): string {
  if (!source) return '';
  return SOURCE_LABELS[source] ?? source;
}

/**
 * Wartość do pokazania. Nieznanej spółki nie ukrywamy — pokazujemy numer,
 * żeby było widać, że wpis wskazuje coś, czego nazwy nie znamy. Tak samo
 * kontrahent (21.10.2026): nazwa z API (`contractorNames`), a usunięty
 * z kartoteki — numerem.
 */
export function changeValueLabel(
  field: string,
  value: string | null | undefined,
  companies: ReadonlyArray<Company>,
  contractorNames: Readonly<Record<string, string>> = {},
): string {
  if (value === null || value === undefined || value === '') return '—';
  if (DATE_FIELDS.has(field)) return wallClockDateTime(value);
  if (field === 'companyId') {
    const company = companies.find((c) => String(c.id) === value);
    return company ? company.name : `spółka #${value}`;
  }
  if (field === 'contractorId') return contractorNames[value] ?? `kontrahent #${value}`;
  if (field === 'utworzenie') {
    if (value === 'COPY') return 'kopia innego prezlecenia, po porównaniu pól';
    return value === 'MANUAL' ? 'wpisane ręcznie' : `źródło: ${changeSourceLabel(value)}`;
  }
  if (field === 'copiedFrom' || field === 'copiedTo') return `prezlecenie #${value}`;
  if (field === 'intakeStage') return INTAKE_STAGE_LABELS[value] ?? value;
  if (field === 'viaWriteStatus') return WRITE_STATE_LABELS[value] ?? value;
  if (field === 'clientQuestion') return QUESTION_STATE_LABELS[value] ?? value;
  if (field === 'inquiryStatus') return INQUIRY_STATUS_LABELS[value] ?? value;
  if (field === 'kind') return value === 'inquiry' ? 'zapytanie klienta' : value;
  return value;
}

/**
 * Miejsce punktu prezlecenia: „PL 60-001 Poznań” — kraj, kod i miasto, ile
 * wpisano (kraj i kod od 06.10.2026); nic — kreska.
 */
export function pointPlace(r: Record<string, unknown>, which: 'loading' | 'unloading'): string {
  const parts = [r[`${which}Country`], r[`${which}PostalCode`], r[`${which}City`]].filter(
    (v): v is string => typeof v === 'string' && v.trim() !== '',
  );
  return parts.length > 0 ? parts.join(' ') : '—';
}

/**
 * Prezlecenie wpisane z ręki: bez maila źródłowego i bez koperty ekstrakcji.
 * Panel „Pola wyciągnięte przez AI” pokazywał mu „0/7” i same kreski, choć
 * nagłówek był kompletny (e2e na dev, 28.09.2026) — nie ma czego oceniać.
 */
export const isManualPreOrder = (r: Record<string, unknown>): boolean =>
  r.sourceEmailId == null && r.extractedFields == null;

